/**
 * 사용 안내 — 두 곳이 같은 내용을 본다:
 *   ① 헤더 「도움말 › 사용 안내」 창 — 사람이 훑어보는 요약
 *   ② AI 어시스턴트의 `get_usage_guide` 도구 — 사용법 질문에 답할 때 읽는 근거
 * 글은 언어별 문서(`content/ko.ts` · `content/en.ts`)에 있고, 여기서는 **지금 언어의 것**을 꺼내 준다.
 *
 * `sampleWorkflow` 는 샘플 프로젝트의 워크플로 이름 앞머리(예: '샘플③')다 — 캔버스에 저장된 **데이터**라
 * 번역하지 않고 여기(코드)에 둔다. 어시스턴트는 `open_sample_workflow` 로 그것을 열고 이어서 설명한다.
 *
 * 설계 원문: datamodeler docs/features/ai-chat/ai-help-agent.md (이식 체크리스트 1번).
 */
import { helpContent } from './content';

export interface UsageTopic {
  id: string;
  title: string;
  summary: string;
  steps: string[];
  /** 샘플 프로젝트 워크플로 이름의 앞머리. 없으면 「샘플로 보기」 버튼이 나오지 않는다 */
  sampleWorkflow?: string;
  /** 이 기능이 있는 화면 자리(help/pointer 의 대상 키) — 어시스턴트가 point_at 으로 가리킨다 */
  point?: string;
}

/** 주제 → 함께 열어 볼 샘플 (이름은 데이터라 언어와 무관하다) */
const SAMPLE_OF: Record<string, string> = {
  start: '샘플①',
  dfd: '샘플③',
  transform: '샘플⑥',
  control: '샘플③',
  quality: '샘플⑩',
};

/** 주제 → 그 기능이 있는 화면 자리 (샘플이 없는 주제에서 「어디」를 짚을 때) */
const POINT_OF: Record<string, string> = {
  migrate: 'header:project',
  compare: 'toolbox:compare',
};

/** 지금 언어의 주제 목록 */
export function usageTopics(): UsageTopic[] {
  return helpContent().topics.map((t) => ({
    ...t,
    ...(SAMPLE_OF[t.id] ? { sampleWorkflow: SAMPLE_OF[t.id] } : {}),
    ...(POINT_OF[t.id] ? { point: POINT_OF[t.id] } : {}),
  }));
}

/** 주제 하나 찾기 — id 또는 제목/요약/단계에 포함된 말로 */
export function findTopic(query?: string | null): UsageTopic | null {
  if (!query) return null;
  const q = String(query).trim().toLowerCase();
  if (!q) return null;
  const topics = usageTopics();
  return topics.find((t) => t.id === q)
    || topics.find((t) => t.title.toLowerCase().includes(q))
    || topics.find((t) => t.summary.toLowerCase().includes(q) || t.steps.some((s) => s.toLowerCase().includes(q)))
    || null;
}

export default usageTopics;
